import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, Instagram as InstagramIcon } from '@mui/icons-material';
import { BRAND, CONTACT } from '../brand';

// ─────────────────────────────────────────────────────────────────────────────
// Reels del carrusel de Home ("Mirá la experiencia en acción").
// Los de /negocios están en ./negocios/contenido.js (VIDEO.reels).
// Para agregar uno: en Instagram, "..." → "Copiar enlace" y pegalo acá.
// Tienen que ser PÚBLICOS. Si un reel se borra o se archiva, Instagram muestra
// "The link to this photo or video may be broken": sacalo de la lista.
// ─────────────────────────────────────────────────────────────────────────────
export const HOME_REELS = [
    'https://www.instagram.com/reel/Dec3Az-uou1/',
    'https://www.instagram.com/reel/DdZTJDLO-hS/',
    'https://www.instagram.com/reel/DeAsmb-OI9H/',
];

const CARD_W = 300;
const CARD_H = 540;
const GAP = 16;

// Acepta /reel/ID, /reels/ID, /p/ID, con o sin parámetros (?igsh=...)
export const embedUrl = (url) => {
    const m = String(url).match(/instagram\.com\/(?:[^/?#]+\/)?(reel|reels|p|tv)\/([A-Za-z0-9_-]+)/);
    if (!m) return null;
    const tipo = m[1] === 'reels' ? 'reel' : m[1];
    return `https://www.instagram.com/${tipo}/${m[2]}/embed/`;
};

/**
 * Carrusel de reels de Instagram.
 * @param reels     lista de URLs de reels
 * @param tema      'oscuro' (fondo oscuro, Home) | 'claro' (fondo blanco, /negocios)
 * @param compacto  true = un reel por vez con flechas (para columnas angostas)
 * @param verMas    mostrar el link "Ver más en @videocallshop.ok"
 */
const InstagramCarousel = ({ reels = HOME_REELS, tema = 'oscuro', compacto = false, verMas = true }) => {
    const validos = reels.filter((r) => embedUrl(r));
    const track = useRef(null);
    const [activo, setActivo] = useState(0);
    const [puedeIzq, setPuedeIzq] = useState(false);
    const [puedeDer, setPuedeDer] = useState(false);

    const claro = tema === 'claro';
    const acento = claro ? BRAND.blueDark || '#0f6fb0' : BRAND.green;
    const paso = () => {
        const el = track.current;
        const card = el && el.firstElementChild;
        return card ? card.offsetWidth + GAP : CARD_W + GAP;
    };

    const actualizar = useCallback(() => {
        const el = track.current;
        if (!el) return;
        setPuedeIzq(el.scrollLeft > 4);
        setPuedeDer(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
        const card = el.firstElementChild;
        const w = card ? card.offsetWidth + GAP : CARD_W + GAP;
        const centro = el.scrollLeft + el.clientWidth / 2 - (card ? card.offsetLeft : 0);
        setActivo(Math.max(0, Math.min(validos.length - 1, Math.floor(centro / w))));
    }, [validos.length]);

    useEffect(() => {
        actualizar();
        window.addEventListener('resize', actualizar);
        return () => window.removeEventListener('resize', actualizar);
    }, [actualizar]);

    const mover = (dir) => {
        const el = track.current;
        if (el) el.scrollBy({ left: dir * paso(), behavior: 'smooth' });
    };
    const irA = (i) => {
        const el = track.current;
        const card = el && el.children[i];
        if (card) el.scrollTo({ left: card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2, behavior: 'smooth' });
    };

    const perfil = `https://instagram.com/${CONTACT.instagram}`;

    // Sin reels cargados: tarjeta que lleva al perfil (nunca un embed roto)
    if (validos.length === 0) {
        return (
            <Box
                component="a"
                href={perfil}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
                    px: 5, py: 4, borderRadius: '14px', textDecoration: 'none', color: 'white',
                    backgroundColor: '#13233A', border: '1px solid rgba(255,255,255,0.1)',
                    '&:hover': { borderColor: BRAND.green },
                }}
            >
                <InstagramIcon sx={{ fontSize: 44, color: BRAND.green }} />
                <Typography sx={{ fontWeight: 600 }}>Mirá los videos en @{CONTACT.instagram}</Typography>
            </Box>
        );
    }

    const flecha = (dir, visible) => (
        <IconButton
            aria-label={dir < 0 ? 'Reel anterior' : 'Reel siguiente'}
            onClick={() => mover(dir)}
            sx={{
                display: compacto ? 'inline-flex' : { xs: 'none', md: 'inline-flex' },
                visibility: visible ? 'visible' : 'hidden',
                position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                [dir < 0 ? 'left' : 'right']: compacto ? { xs: -6, sm: -22 } : -8,
                zIndex: 2,
                backgroundColor: BRAND.green, color: BRAND.night,
                boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                '&:hover': { backgroundColor: BRAND.greenDark || BRAND.green },
            }}
        >
            {dir < 0 ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
    );

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: compacto ? CARD_W : 3 * CARD_W + 2 * GAP + 48,
                mx: 'auto',
            }}
        >
            <Box sx={{ position: 'relative', px: compacto ? 0 : { xs: 0, md: 3 } }}>
                {flecha(-1, puedeIzq)}
                <Box
                    ref={track}
                    onScroll={actualizar}
                    sx={{
                        display: 'flex',
                        gap: `${GAP}px`,
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        justifyContent: !compacto && validos.length < 3 ? { md: 'center' } : 'flex-start',
                        px: compacto ? 0 : { xs: `calc(50% - ${CARD_W / 2}px)`, md: 0 },
                        pb: 1,
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}
                >
                    {validos.map((url, i) => (
                        <Box
                            key={url}
                            sx={{
                                flex: compacto ? '0 0 100%' : `0 0 ${CARD_W}px`,
                                height: CARD_H,
                                scrollSnapAlign: 'center',
                                borderRadius: '14px',
                                overflow: 'hidden',
                                border: claro ? '1px solid #dde7f1' : '1px solid rgba(255,255,255,0.1)',
                                backgroundColor: claro ? '#ffffff' : '#13233A',
                                boxShadow: claro ? '0 22px 50px -24px rgba(15,27,45,0.45)' : 'none',
                            }}
                        >
                            <iframe
                                src={embedUrl(url)}
                                frameBorder="0"
                                scrolling="no"
                                loading="lazy"
                                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                                title={`Reel de VideoCallShop ${i + 1}`}
                                style={{ display: 'block', border: 'none', width: '100%', height: '100%' }}
                            />
                        </Box>
                    ))}
                </Box>
                {flecha(1, puedeDer)}
            </Box>

            {validos.length > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                    {validos.map((url, i) => (
                        <Box
                            key={url}
                            component="button"
                            type="button"
                            aria-label={`Ver reel ${i + 1}`}
                            aria-current={i === activo ? 'true' : undefined}
                            onClick={() => irA(i)}
                            sx={{
                                width: i === activo ? 22 : 8, height: 8, p: 0, border: 0, cursor: 'pointer',
                                borderRadius: '999px', transition: 'all .25s',
                                backgroundColor: i === activo
                                    ? (claro ? BRAND.blue : BRAND.green)
                                    : (claro ? 'rgba(15,27,45,0.2)' : 'rgba(255,255,255,0.3)'),
                            }}
                        />
                    ))}
                </Box>
            )}

            {verMas && (
                <Box sx={{ textAlign: 'center', mt: 2.5 }}>
                    <Box
                        component="a"
                        href={perfil}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: acento, fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                    >
                        <InstagramIcon fontSize="small" /> Ver más en @{CONTACT.instagram}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default InstagramCarousel;
